/**
 * FE active-currency context (design D21 wiring, Task 2.9).
 * Mirrors the server twin (src/server/services/currency-context.ts):
 * isBase-aware resolution, fail-soft fallback on an unknown code, and the
 * same shouldShowCurrencyCode semantics — but held as simple in-memory
 * module state instead of a per-request DB read, since the FE fetches the
 * active list once (see the bootstrap wiring in AuthContext.tsx) rather
 * than per render.
 *
 * Defaults to a USD-only context so every page that hasn't been wired to
 * call setCurrencyContext yet (all of them, until Task 2.10) renders
 * exactly like the pre-multi-currency portal — this is the neutrality
 * mechanism for the FE side of D21.
 */
import { shouldShowCurrencyCode, USD_CURRENCY, type MoneyCurrency } from './money.js';

/**
 * A currency descriptor plus its rate-freshness metadata (Task 2.10).
 * `usdRateUpdatedAt` is NOT part of the money.ts twin — formatMoney /
 * roundMoney / shouldShowCurrencyCode never need it — it exists purely for
 * the converted-line tag's "rate last updated" tooltip (D12). Optional, so
 * every existing `MoneyCurrency` fixture/caller (including USD_CURRENCY)
 * stays valid unmodified.
 */
export interface CurrencyContextEntry extends MoneyCurrency {
  usdRateUpdatedAt?: string | null;
}

const DEFAULT_BY_CODE: Record<string, CurrencyContextEntry> = { USD: USD_CURRENCY };

let byCode: Record<string, CurrencyContextEntry> = { ...DEFAULT_BY_CODE };
let count = 1;

/**
 * Replace the active-currency context, e.g. from GET /currencies/active.
 * Defensive: an empty/missing list is ignored rather than blanking the app
 * out to zero known currencies (a transient fetch glitch must never break
 * every currency-aware render on the page).
 */
export function setCurrencyContext(currencies: CurrencyContextEntry[] | null | undefined): void {
  if (!currencies || currencies.length === 0) return;
  const next: Record<string, CurrencyContextEntry> = {};
  for (const currency of currencies) {
    next[currency.code.trim().toUpperCase()] = currency;
  }
  byCode = next;
  count = currencies.length;
}

/** Test-only: restore the USD-only default. Also safe to call in production (no-op risk). */
export function resetCurrencyContext(): void {
  byCode = { ...DEFAULT_BY_CODE };
  count = 1;
}

function unknownCurrencyFallback(code: string): CurrencyContextEntry {
  return {
    code,
    symbol: '$',
    displayCode: code,
    decimalPlaces: 2,
    roundingIncrement: 0.01,
    isBase: false,
  };
}

/**
 * Resolve the display descriptor for a currency code. Fail-soft: an
 * unknown/not-yet-loaded code never throws — it renders with a generic
 * descriptor (code visible) and a console.warn, same contract as the
 * server twin's resolveDisplayCurrency.
 */
export function getCurrencyDescriptor(code: string | null | undefined): CurrencyContextEntry {
  const normalized = (code || 'USD').trim().toUpperCase();
  const known = byCode[normalized];
  if (known) return known;
  console.warn(
    `[currencyContext] Unknown currency code "${normalized}" — rendering with a generic fallback descriptor (code shown).`,
  );
  return unknownCurrencyFallback(normalized);
}

/** Whether a currency code should print its code suffix (D21) under the current context. */
export function shouldShowCode(code: string | null | undefined): boolean {
  return shouldShowCurrencyCode(getCurrencyDescriptor(code), count);
}

/** The number of currently-active currencies, per the last setCurrencyContext call. */
export function getActiveCurrencyCount(): number {
  return count;
}

/**
 * Whether `code` is a genuinely REGISTERED active currency — unlike
 * getCurrencyDescriptor, this never fails soft to a generic fallback. Used
 * for real yes/no gates (Task 2.10: "is CAD active?" for the D18 address
 * hint) where a fabricated fallback descriptor would wrongly read as "yes".
 */
export function isCurrencyActive(code: string | null | undefined): boolean {
  const normalized = (code || '').trim().toUpperCase();
  if (!normalized) return false;
  return normalized in byCode;
}

/** Every currently-active currency descriptor — feeds the quote-header currency Select's options (Task 2.10). */
export function getActiveCurrencyDescriptors(): CurrencyContextEntry[] {
  return Object.values(byCode);
}
